// @ts-nocheck: converted from JS

import { FieldTypes } from '@/components/FormBuilder/Item.utils';
import { DateTimePicker, Number, Radio, Select, Text, Textarea } from '@/components/UI/FormFields';
import {
  DateTimePicker as DateTimePickerLatest,
  Number as NumberLatest,
  Select as SelectLatest,
  Text as TextLatest,
  SelectLookup as SelectLookupLatest,
} from '@/components/UI/FormFields/Latest';
import {
  Datepicker as ReadOnlyDatepicker,
  Select as ReadOnlySelect,
  Text as ReadOnlyText,
} from '@/components/UI/FormFields/ReadOnly';
import ReadOnlyDatepickerLatest from '@/components/UI/FormFields/ReadOnly/Latest/Datepicker';
import ReadOnlySelectLatest from '@/components/UI/FormFields/ReadOnly/Latest/Select';
import ReadOnlyTextLatest from '@/components/UI/FormFields/ReadOnly/Latest/Text';
import SelectLookup from '@/components/UI/FormFields/SelectLookup';
import Summary, { SummaryItem } from '@/components/UI/Summary';
import type { RequestTemplateGroups } from '@/model/Request.model';
import { FC, PropsWithChildren, ReactNode, useEffect } from 'react';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';

interface FormRenderProps {
  fields?: Array<{ name: string; type: string; slug?: string }>;
  groups?: Array<RequestTemplateGroups>;
  groupContainer?: ReactNode | FC<PropsWithChildren>;
  disabled?: boolean;
  submitted?: boolean;
  studyCreate?: boolean;
  testPrefix?: string;
  hookFormVersion?: 'legacy' | 'latest';
  invalidFields?: Set<string>;
}

const FormRender: FC<FormRenderProps> = ({
  fields,
  groups,
  groupContainer,
  disabled = false,
  submitted = false,
  studyCreate = false,
  testPrefix = 'metadata-field',
  hookFormVersion = 'legacy',
  invalidFields,
}) => {
  const { setStepReady } = useStudyCreateStep();

  useEffect(() => {
    if (studyCreate) {
      setStepReady(true);
    }
  }, [studyCreate, setStepReady]);

  const getComponent = (type, props) => {
    const testId = `${testPrefix}__${props.slug ?? type}`;
    const metadataType = studyCreate ? 'study_meta' : undefined;
    const hasError = invalidFields?.has(props.slug) ?? false;

    const componentMap = {
      [FieldTypes.text]:
        hookFormVersion !== 'legacy' ? (
          <TextLatest {...props} testId={testId} metadataType={metadataType} />
        ) : (
          <Text {...props} testId={testId} metadataType={metadataType} />
        ),
      [FieldTypes.textarea]: <Textarea {...props} testId={testId} metadataType={metadataType} />,
      [FieldTypes.select]:
        hookFormVersion !== 'legacy' ? (
          <SelectLatest {...props} testId={testId} metadataType={metadataType} hasError={hasError} />
        ) : (
          <Select {...props} testId={testId} metadataType={metadataType} hasError={hasError} />
        ),
      [FieldTypes.multi_select]:
        hookFormVersion !== 'legacy' ? (
          <SelectLatest {...props} isMulti testId={testId} metadataType={metadataType} hasError={hasError} />
        ) : (
          <Select {...props} isMulti testId={testId} metadataType={metadataType} hasError={hasError} />
        ),
      [FieldTypes.date]:
        hookFormVersion !== 'legacy' ? (
          <DateTimePickerLatest {...props} testId={testId} metadataType={metadataType} />
        ) : (
          <DateTimePicker {...props} testId={testId} metadataType={metadataType} />
        ),
      [FieldTypes.radio]: <Radio {...props} testId={testId} metadataType={metadataType} />,
      [FieldTypes.lookup_select]:
        hookFormVersion !== 'legacy' ? (
          <SelectLookupLatest {...props} testId={testId} metadataType={metadataType} />
        ) : (
          <SelectLookup {...props} testId={testId} metadataType={metadataType} />
        ),
      [FieldTypes.lookup_multi_select]:
        hookFormVersion !== 'legacy' ? (
          <SelectLookupLatest {...props} isMulti testId={testId} metadataType={metadataType} />
        ) : (
          <SelectLookup {...props} isMulti testId={testId} metadataType={metadataType} />
        ),
      [FieldTypes.numeric]:
        hookFormVersion !== 'legacy' ? (
          <NumberLatest {...props} testId={testId} metadataType={metadataType} />
        ) : (
          <Number {...props} testId={testId} metadataType={metadataType} />
        ),
    };
    return componentMap[type];
  };

  const getReadOnlyComponent = (type, props) => {
    const { name } = props;
    const isLatest = hookFormVersion !== 'legacy';
    const formatMap = {
      [FieldTypes.text]: isLatest ? <ReadOnlyTextLatest {...props} /> : <ReadOnlyText {...props} />,
      [FieldTypes.textarea]: isLatest ? <ReadOnlyTextLatest {...props} /> : <ReadOnlyText {...props} />,
      [FieldTypes.select]: isLatest ? (
        <ReadOnlySelectLatest name={name} isMulti={false} />
      ) : (
        <ReadOnlySelect name={name} isMulti={false} />
      ),
      [FieldTypes.multi_select]: isLatest ? (
        <ReadOnlySelectLatest name={name} isMulti={true} />
      ) : (
        <ReadOnlySelect name={name} isMulti={true} />
      ),
      [FieldTypes.date]: isLatest ? <ReadOnlyDatepickerLatest name={name} /> : <ReadOnlyDatepicker name={name} />,
      [FieldTypes.radio]: isLatest ? <ReadOnlyTextLatest {...props} /> : <ReadOnlyText {...props} />,
    };
    return formatMap[type];
  };

  const GroupContainer = groupContainer || 'div';
  return (
    <div data-test-component="FormRender" data-test-element="container" data-testid="metadata-field-group">
      {fields?.map((field) => (
        <div
          key={field.name}
          className="mb4"
          data-test-element="field-container"
          data-test-key={field.slug}
          data-testid={`metadata-field-group__${field.slug}`}
        >
          {getComponent(field.type, {
            ...field,
            disabled,
          })}
        </div>
      ))}
      {groups &&
        (submitted ? (
          <div className="mw7 w-100" data-test-element="submitted-group-container">
            {groups.map((group) => (
              <Summary key={group.name} title={group.label}>
                {group.fields.map((field) => (
                  <SummaryItem key={field.name} label={field.label}>
                    {getReadOnlyComponent(field.type, field)}
                  </SummaryItem>
                ))}
              </Summary>
            ))}
          </div>
        ) : (
          groups.map((group) => (
            <GroupContainer key={group.name}>
              <>
                {group.label && (
                  <h2 className="f4 normal lh-title mb4" data-test-element="group-label" data-test-key={group.name}>
                    {group.label}
                  </h2>
                )}
                {group.fields.map((field) => (
                  <div
                    key={field.name}
                    className="mb4"
                    data-test-element="group-field"
                    data-test-key={field.name}
                    data-testid={`group-field-${field.type}`}
                  >
                    {getComponent(field.type, {
                      ...field,
                      disabled,
                    })}
                  </div>
                ))}
              </>
            </GroupContainer>
          ))
        ))}
    </div>
  );
};

export default FormRender;
